import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, ArrowLeft, Phone, Mail } from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import InquiryForm from '../../components/support/InquiryForm';
import Button from '../../components/common/Button';
import Card from '../../components/common/Card';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';

/**
 * InquiryCreatePage - 1:1 문의 작성 페이지
 */
const InquiryCreatePage = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  // 로그인 체크
  React.useEffect(() => {
    if (!user) {
      showToast('로그인이 필요한 서비스입니다.', 'warning');
      navigate('/login', { state: { from: '/support/inquiry' } });
    }
  }, [user, navigate, showToast]);

  const handleSubmit = async (formData) => {
    if (submitting) return; // 중복 제출 방지
    setSubmitting(true);
    try {
      // Mock 문의 제출 (실제 저장 없이 제출 흐름만 시연)
      await new Promise((resolve) => setTimeout(resolve, 800));
      console.log('문의 제출:', {
        ...formData,
        userId: user.id,
        status: 'pending',
        createdAt: new Date().toISOString(),
        answeredAt: null,
        answer: null,
      });

      showToast('문의가 성공적으로 제출되었습니다.', 'success');
      // 문의 목록으로 이동 (실제 라우트: /support/inquiries)
      navigate('/support/inquiries');
    } catch (error) {
      console.error('Failed to submit inquiry:', error);
      showToast('문의 제출에 실패했습니다. 다시 시도해주세요.', 'error');
      setSubmitting(false);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* 뒤로 가기 */}
        <Button
          variant="outline"
          onClick={() => navigate(-1)}
          className="mb-6"
        >
          <ArrowLeft size={16} className="mr-2" />
          뒤로 가기
        </Button>

        {/* 헤더 */}
        <div className="mb-6 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <MessageSquare size={32} className="text-primary-600" />
          </div>
          <h1 className="text-2xl font-bold text-primary-900 mb-2">1:1 문의하기</h1>
          <p className="text-sm text-accent-600">
            궁금하신 내용을 남겨주시면 빠르게 답변드리겠습니다
          </p>
        </div>

        {/* 문의 작성 폼 */}
        <Card>
          <InquiryForm onSubmit={handleSubmit} submitting={submitting} />
        </Card>

        {/* 안내 사항 */}
        <div className="mt-6 p-4 bg-secondary-50 rounded-lg border border-accent-200">
          <h3 className="font-semibold text-primary-900 mb-2">문의 전 확인해주세요</h3>
          <ul className="text-sm text-accent-600 space-y-1">
            <li>• 평균 답변 시간은 영업일 기준 1-2일입니다.</li>
            <li>• 급하신 경우 고객센터(1588-XXXX)로 전화 문의해주세요.</li>
            <li>• FAQ에서 자주 묻는 질문을 먼저 확인해보세요.</li>
          </ul>
        </div>

        {/* 고객센터 정보 */}
        <div className="mt-4 p-4 bg-white rounded-lg border border-accent-200">
          <h3 className="font-semibold text-primary-900 mb-2">고객센터 안내</h3>
          <div className="text-sm text-accent-600 space-y-2">
            <a
              href="tel:1588-0000"
              className="flex items-center gap-2 hover:text-primary-700"
            >
              <Phone size={16} /> 전화: 1588-0000
            </a>
            <p className="flex items-center gap-2">⏰ 운영시간: 평일 09:00 - 18:00</p>
            <a
              href="mailto:support@funeral-service.com"
              className="flex items-center gap-2 hover:text-primary-700"
            >
              <Mail size={16} /> 이메일: support@funeral-service.com
            </a>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default InquiryCreatePage;
